import { Link } from "react-router-dom";
import {
  Printer, Download, ArrowLeft, ShoppingCart, Wrench, CheckCircle2,
  ClipboardCheck, Truck, Layers, Recycle, ShieldCheck, TrendingDown,
  Search, PackageCheck, Route as RouteIcon, DollarSign, MapPin, ArrowRight,
} from "lucide-react";
import { IMAGES, COMPANY } from "../lib/data";

const TBC = ({ children }) => (
  <span className="inline-block border border-dashed border-amber-500 bg-amber-50 text-amber-700 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider rounded-sm">
    {children}
  </span>
);

const Slide = ({ children, n, total, kicker, testid }) => (
  <div
    data-testid={testid}
    className="bg-white shadow-2xl print:shadow-none w-[297mm] h-[210mm] mx-auto mb-8 print:mb-0 relative flex flex-col"
    style={{ breakAfter: n === total ? "auto" : "page", padding: "16mm" }}
  >
    <div className="flex items-center justify-between border-b border-border pb-3">
      <div className="flex items-center gap-2">
        <img src={IMAGES.markDark} alt="OneHarvest" className="h-6 w-6 object-contain" />
        <span className="font-display font-extrabold tracking-tight text-ink text-sm">ONEHARVESTCO.</span>
      </div>
      <span className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">{kicker}</span>
    </div>
    <div className="flex-1 min-h-0 pt-6 flex flex-col">{children}</div>
    <div className="pt-3 border-t border-border flex items-center justify-between text-[10px] text-muted-foreground">
      <span>Ground Protection Solutions · Buy vs. Service</span>
      <span>{n} / {total}</span>
    </div>
  </div>
);

const CardCol = ({ tag, icon: Icon, title, sub, children }) => (
  <div className="border border-border flex flex-col">
    <div className="bg-ink text-white px-5 py-4 flex items-center gap-3">
      <span className="font-display font-black text-flame text-3xl leading-none">{tag}</span>
      <div className="flex-1">
        <div className="text-[10px] uppercase tracking-[0.18em] text-flame">{sub}</div>
        <div className="font-display font-bold text-xl leading-tight">{title}</div>
      </div>
      <Icon className="h-8 w-8 text-white/25" />
    </div>
    <div className="p-5 flex-1">{children}</div>
  </div>
);

const Bullet = ({ children }) => (
  <div className="flex items-start gap-2.5">
    <CheckCircle2 className="h-4 w-4 text-flame flex-shrink-0 mt-[2px]" />
    <span className="text-charcoal text-[13px] leading-snug">{children}</span>
  </div>
);

const TOTAL = 7;

export default function GroundProtectionDeck() {
  return (
    <div className="min-h-screen bg-charcoal/20 print:bg-white">
      {/* top bar */}
      <div className="sticky top-0 z-40 bg-ink text-white border-b border-white/10 print:hidden">
        <div className="max-w-6xl mx-auto px-6 h-14 flex items-center justify-between">
          <Link to="/solutions" className="inline-flex items-center gap-2 text-white/70 hover:text-flame text-sm">
            <ArrowLeft className="h-4 w-4" /> Back to solutions
          </Link>
          <div className="flex items-center gap-3">
            <span className="hidden sm:inline text-[11px] uppercase tracking-widest text-white/50">Two Avenues · {TOTAL}-Slide Deck</span>
            <a href="/ground-protection-deck.pdf" download data-testid="download-gp-deck" className="inline-flex items-center gap-2 border border-white/25 text-white px-4 py-2 text-sm font-semibold hover:border-flame hover:text-flame transition-colors">
              <Download className="h-4 w-4" /> Download PDF
            </a>
            <button onClick={() => window.print()} data-testid="print-gp-deck" className="inline-flex items-center gap-2 bg-flame text-white px-4 py-2 text-sm font-semibold hover:bg-flame/90 transition-colors">
              <Printer className="h-4 w-4" /> Print
            </button>
          </div>
        </div>
      </div>

      <div className="py-8 print:py-0" style={{ fontSize: "12px" }}>

        {/* ===== 1 · TITLE ===== */}
        <Slide n={1} total={TOTAL} kicker="Overview" testid="slide-1">
          <div className="flex-1 flex flex-col justify-center">
            <span className="inline-flex w-fit items-center border border-flame/40 bg-flame/10 text-flame px-3 py-1 text-[11px] uppercase tracking-[0.2em]">Ground Protection Solutions</span>
            <h1 className="mt-5 font-display font-black tracking-tight text-ink leading-[0.98] text-[64px] max-w-[85%]">
              Two avenues to<br />site access.
            </h1>
            <p className="mt-5 text-charcoal text-[18px] leading-snug max-w-[72%]">
              We procure or rent <span className="font-semibold text-ink">any mat, from any manufacturer</span> — flagship
              <span className="font-semibold text-ink"> Big Bad Mats</span> — to keep crews and equipment moving.
              <span className="font-semibold text-ink"> Buy the product</span> and run your own fleet, or
              <span className="font-semibold text-ink"> choose our full turnkey service</span> and we handle everything.
            </p>
            <div className="mt-8 flex items-center gap-8">
              <div className="flex items-center gap-3">
                <div className="h-11 w-11 bg-ink text-flame flex items-center justify-center"><ShoppingCart className="h-5 w-5" /></div>
                <div>
                  <div className="font-display font-bold text-ink text-[15px]">A · Buy the Product</div>
                  <div className="text-[11px] text-muted-foreground uppercase tracking-wider">Any mat that fits · you own</div>
                </div>
              </div>
              <ArrowRight className="h-5 w-5 text-muted-foreground" />
              <div className="flex items-center gap-3">
                <div className="h-11 w-11 bg-flame text-white flex items-center justify-center"><Wrench className="h-5 w-5" /></div>
                <div>
                  <div className="font-display font-bold text-ink text-[15px]">B · Turnkey Service</div>
                  <div className="text-[11px] text-muted-foreground uppercase tracking-wider">Any mat that fits · done for you</div>
                </div>
              </div>
            </div>
          </div>
        </Slide>

        {/* ===== 2 · WHY IT MATTERS ===== */}
        <Slide n={2} total={TOTAL} kicker="The challenge" testid="slide-2">
          <h2 className="font-display font-black text-ink text-[40px] leading-tight max-w-[80%]">Bad ground stops good work.</h2>
          <p className="mt-3 text-charcoal text-[15px] max-w-[75%]">Soft, sensitive, or regulated terrain slows mobilization, damages equipment, and creates environmental and safety exposure. The right ground-protection system protects the site — and the schedule.</p>
          <div className="mt-8 grid grid-cols-3 gap-6 flex-1">
            {[
              { icon: TrendingDown, t: "Downtime & delay", d: "Stuck equipment and rutted access roads push mobilization and blow milestones." },
              { icon: ShieldCheck, t: "Safety & compliance", d: "Unstable footing and disturbed ground raise incident risk and regulatory exposure." },
              { icon: Recycle, t: "Site & restoration cost", d: "Damaged terrain means costly remediation and reclamation once the job is done." },
            ].map((c) => (
              <div key={c.t} className="border border-border p-6 flex flex-col">
                <c.icon className="h-9 w-9 text-flame" />
                <div className="mt-4 font-display font-bold text-ink text-[18px]">{c.t}</div>
                <p className="mt-2 text-charcoal text-[13px] leading-snug">{c.d}</p>
              </div>
            ))}
          </div>
          <p className="mt-6 text-ink text-[15px] font-medium">OneHarvestCo. solves it two ways — you choose how much to own and how much to hand off.</p>
        </Slide>

        {/* ===== 3 · TWO AVENUES AT A GLANCE ===== */}
        <Slide n={3} total={TOTAL} kicker="At a glance" testid="slide-3">
          <h2 className="font-display font-black text-ink text-[36px] leading-tight">Any mat, flagship Big Bad Mats. Two ways to deploy.</h2>
          <div className="mt-6 grid grid-cols-2 gap-6 flex-1">
            <CardCol tag="A" icon={ShoppingCart} title="Buy the Product" sub="Any mat that fits · you own">
              <p className="text-charcoal text-[13px] leading-snug">Buy any mat from any manufacturer — flagship Big Bad Mats — and own a reusable fleet you control.</p>
              <div className="mt-4 space-y-2.5">
                <Bullet>Procure any mat, sourced to your spec</Bullet>
                <Bullet>Flagship Big Bad Mats for lowest cost-per-use</Bullet>
                <Bullet>Own the fleet & self-deploy on your schedule</Bullet>
              </div>
            </CardCol>
            <CardCol tag="B" icon={Wrench} title="Turnkey Service" sub="Any mat that fits · we handle it">
              <p className="text-charcoal text-[13px] leading-snug">Hand off the whole job — we source, deliver, install, maintain, and remove the right matting (rental included). No capital, no logistics.</p>
              <div className="mt-4 space-y-2.5">
                <Bullet>Any mat sourced &amp; managed for you</Bullet>
                <Bullet>Zero capital — pay per project, no storage</Bullet>
                <Bullet>One accountable partner, end to end</Bullet>
              </div>
            </CardCol>
          </div>
        </Slide>

        {/* ===== 4 · AVENUE A DEEP DIVE ===== */}
        <Slide n={4} total={TOTAL} kicker="Avenue A" testid="slide-4">
          <div className="flex items-center gap-3">
            <span className="font-display font-black text-flame text-5xl leading-none">A</span>
            <div>
              <div className="text-[11px] uppercase tracking-[0.2em] text-flame">Any mat that fits · you own</div>
              <h2 className="font-display font-black text-ink text-[34px] leading-none">Buy the Product</h2>
            </div>
          </div>
          <p className="mt-4 text-charcoal text-[15px] max-w-[85%]">Buy any mat from any manufacturer — with the flagship Big Bad Mats when durability matters most — and build a reusable fleet you own. Ideal when site access is a recurring need and you want the lowest long-term cost with full control.</p>
          <div className="mt-6 grid grid-cols-2 gap-8 flex-1">
            <div>
              <div className="text-[11px] uppercase tracking-[0.18em] text-flame mb-3">What you get</div>
              <div className="space-y-2.5">
                <Bullet>Any mat, any manufacturer — sourced to your spec</Bullet>
                <Bullet>Flagship Big Bad Mats: certified ratings & long life</Bullet>
                <Bullet>Ramps, connectors & accessories to spec</Bullet>
                <Bullet>Volume pricing that improves cost-per-use</Bullet>
                <Bullet>Complete control over when and where you deploy</Bullet>
              </div>
            </div>
            <div className="bg-muted border border-border p-6 flex flex-col">
              <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground mb-2">The economics</div>
              <p className="text-charcoal text-[14px] leading-snug">Every reuse spreads the one-time purchase across more deployments — so cost-per-use keeps falling. For high-frequency crews, ownership wins on total cost of ownership.</p>
              <div className="mt-auto pt-4 border-t border-border">
                <div className="text-[11px] uppercase tracking-[0.18em] text-flame mb-1">Best for</div>
                <p className="text-ink text-[14px] font-medium leading-snug">In-house crews with recurring site-access needs who want the lowest long-term cost and full control.</p>
              </div>
            </div>
          </div>
        </Slide>

        {/* ===== 5 · AVENUE B DEEP DIVE ===== */}
        <Slide n={5} total={TOTAL} kicker="Avenue B" testid="slide-5">
          <div className="flex items-center gap-3">
            <span className="font-display font-black text-flame text-5xl leading-none">B</span>
            <div>
              <div className="text-[11px] uppercase tracking-[0.2em] text-flame">Any mat that fits · turnkey, done for you</div>
              <h2 className="font-display font-black text-ink text-[34px] leading-none">Turnkey Service</h2>
            </div>
          </div>
          <p className="mt-4 text-charcoal text-[15px] max-w-[85%]">Hand off the whole job — we source any mat that fits (flagship Big Bad Mats) and build, protect, and restore the site for you or the client you serve, rental included. No purchase, no storage, no logistics. Just site access, handled.</p>
          <div className="mt-6 text-[11px] uppercase tracking-[0.18em] text-flame">How it works</div>
          <div className="mt-3 grid grid-cols-4 gap-4 flex-1">
            {[
              { icon: Search, t: "Assess", d: "Site assessment & access plan for your job." },
              { icon: Truck, t: "Deliver & install", d: "We ship and professionally install the system." },
              { icon: ClipboardCheck, t: "Maintain", d: "On-project upkeep & repositioning as work moves." },
              { icon: PackageCheck, t: "Remove & restore", d: "Teardown, removal & site restoration when done." },
            ].map((s, i) => (
              <div key={s.t} className="border border-border p-5 flex flex-col relative">
                <span className="absolute top-3 right-3 font-display font-black text-ink/10 text-2xl">{i + 1}</span>
                <s.icon className="h-8 w-8 text-flame" />
                <div className="mt-3 font-display font-bold text-ink text-[16px]">{s.t}</div>
                <p className="mt-1.5 text-charcoal text-[12px] leading-snug">{s.d}</p>
              </div>
            ))}
          </div>
          <div className="mt-5 bg-ink text-white px-6 py-4 flex items-center gap-3">
            <div className="text-[11px] uppercase tracking-[0.18em] text-flame flex-shrink-0">Best for</div>
            <p className="text-[14px] leading-snug">One-off or short-duration projects, teams with no storage or handling capacity, and contractors delivering turnkey site access to their own end-clients.</p>
          </div>
        </Slide>

        {/* ===== 6 · SIDE-BY-SIDE ===== */}
        <Slide n={6} total={TOTAL} kicker="Compare" testid="slide-6">
          <h2 className="font-display font-black text-ink text-[36px] leading-tight">Which path is right for you?</h2>
          <div className="mt-6 border border-border flex-1 overflow-hidden">
            <table className="w-full h-full border-collapse">
              <thead>
                <tr className="bg-muted">
                  <th className="text-left px-6 py-3 w-[24%] text-[11px] uppercase tracking-[0.14em] text-muted-foreground"></th>
                  <th className="text-left px-6 py-3 border-l border-border">
                    <span className="font-display font-bold text-ink text-[17px]">A · Buy the Product</span>
                  </th>
                  <th className="text-left px-6 py-3 border-l border-border">
                    <span className="font-display font-bold text-flame text-[17px]">B · Turnkey Service</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {[
                  { k: "Mat selection", a: "Any mat · flagship Big Bad Mats", b: "Any mat · flagship Big Bad Mats" },
                  { k: "Capital", a: "One-time purchase (CapEx)", b: "No purchase — pay per project (OpEx)" },
                  { k: "Ownership", a: "You own & keep the fleet", b: "We retain & manage the fleet" },
                  { k: "Logistics", a: "You store, haul & deploy", b: "We handle everything, end to end" },
                  { k: "Maintenance", a: "Your crew's responsibility", b: "Included in the service" },
                  { k: "Best when", a: "Frequent, repeat, in-house use", b: "Project-based work / no overhead" },
                ].map((row, i) => (
                  <tr key={row.k} className={i % 2 ? "bg-muted/40" : ""}>
                    <td className="px-6 py-3 text-[13px] font-semibold uppercase tracking-wider text-charcoal">{row.k}</td>
                    <td className="px-6 py-3 text-[14px] text-charcoal border-l border-border">{row.a}</td>
                    <td className="px-6 py-3 text-[14px] text-charcoal border-l border-border">{row.b}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Slide>

        {/* ===== 7 · SERVICE FACTS + CTA ===== */}
        <Slide n={7} total={TOTAL} kicker="Get started" testid="slide-7">
          <h2 className="font-display font-black text-ink text-[36px] leading-tight">Let's get your site ready.</h2>
          <p className="mt-3 text-charcoal text-[15px] max-w-[75%]">Tell us about the job and we'll recommend the right avenue — and price it for you.</p>
          <div className="mt-6 grid grid-cols-3 gap-6 flex-1">
            {[
              { icon: DollarSign, t: "Pricing model", d: <><TBC>To confirm</TBC> — e.g. per-project quote or rental + install fee.</> },
              { icon: ClipboardCheck, t: "What's included", d: <>Site assessment, delivery, installation, maintenance, removal & restoration.</> },
              { icon: MapPin, t: "Coverage & response", d: <><TBC>To confirm</TBC> — service area and typical response time.</> },
            ].map((c) => (
              <div key={c.t} className="border border-border p-6 flex flex-col">
                <c.icon className="h-9 w-9 text-flame" />
                <div className="mt-4 font-display font-bold text-ink text-[17px]">{c.t}</div>
                <p className="mt-2 text-charcoal text-[13px] leading-snug">{c.d}</p>
              </div>
            ))}
          </div>
          <div className="mt-6 bg-ink text-white px-8 py-6 flex items-center justify-between">
            <div>
              <div className="font-display font-black text-[24px] leading-tight">Buy the product, or go full turnkey — any mat.</div>
              <div className="text-white/60 text-[13px] mt-1">Talk to your Environmental Solutions Manager — {COMPANY.phone} · {COMPANY.email}</div>
            </div>
            <Link to="/request-quote" className="inline-flex items-center gap-2 bg-flame text-white px-6 py-3 font-semibold text-[15px] hover:bg-flame/90 transition-colors print:hidden">
              Request a Quote <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </Slide>

        <p className="max-w-6xl mx-auto px-6 mt-2 text-[10px] text-muted-foreground print:hidden">
          © {new Date().getFullYear()} {COMPANY.legal}. Informational overview; inclusions, pricing & coverage confirmed in your quote. Not a contract or guarantee.
        </p>
      </div>
    </div>
  );
}
